// src/modules/student-exit/components/StudentExitTable.tsx
import Datatable from "components/Common/dataTable";
import { useTranslation } from "react-i18next";
import React from "react";
import { Tag, Alert } from "antd";
import { formatToLocaleDate } from "utils/dateUtils";
import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";
import { showConfirmDialog } from "utils/confirmDialogUtils";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";
import StudentExitStatusBadge from "components/Common/Badge/StudentExitStatusBadge";
import CustomAlert from "components/Common/ui/CustomAlert";
import { useAbility } from "config/abilityContext";
import ExitTypeBadge from "components/Common/Badge/ExitTypeBadge";

type Props = {
    dataSource: any[];
    loading?: boolean;
    onApprove?: (record: any) => void;
    onReject?: (record: any) => void;
    onComplete?: (record: any) => void;
    onDelete?: (record: any) => void;
    onEdit?: (record: any) => void;
    dataPaginate: any;
    onChange: (pg: any) => void;
};

const StudentExitTable: React.FC<Props> = ({
    dataSource,
    loading,
    onApprove,
    onReject,
    onComplete,
    onDelete,
    dataPaginate,
    onChange,
    onEdit
}) => {
    const { t } = useTranslation();
    const ability = useAbility();



    // const handleApprove = (record: any) => {
    //     showConfirmDialog({
    //         title: t("studentExit.confirmApprove.title"),
    //         content: t("studentExit.confirmApprove.message"),
    //         onOk: () => onApprove?.(record.id),
    //     });
    // };

    const handleReject = (record: any) => {
        showConfirmDialog({
            title: t("studentExit.confirmReject.title"),
            content: t("studentExit.confirmReject.message"),
            onOk: () => onReject?.(record.id),
            okType: "danger",
        });
    };

    const handleComplete = (record: any) => {
        showConfirmDialog({
            title: t("studentExit.confirmComplete.title"),
            content: t("studentExit.confirmComplete.message"),
            onOk: () => onComplete?.(record.id),
            okType: "primary",
        });
    };

    const handleDelete = (record: any) => {
        showConfirmDialog({
            title: t("studentExit.confirmDelete.title"),
            content: t("studentExit.confirmDelete.message"),
            onOk: () => onDelete?.(record.id),
            okType: "danger",
        });
    };

    const columns = [
        {
            title: t("studentExit.form.student"),
            key: "student",
            render: (_: any, record: any) => (
                <UserProfileHeader
                    firstName={record.student.user.firstName}
                    lastName={record.student.user.lastName}
                    photo={record.student.user.photo}
                    subtitle={record.student.massarCode}
                />
            ),
        },
        {
            title: t("studentExit.form.pickupPerson"),
            key: "pickupPerson",
            render: (_: any, record: any) => (
                <span>
                    {record.authorizedBy ?
                        `${record.authorizedBy.fullName} (${t('studentExit.pickupType.authorized')})` :
                        record.guardian ?
                            `${record.guardian.user.firstName} ${record.guardian.user.lastName} (${t('studentExit.pickupType.guardian')})` :
                            '—'
                    }
                </span>
            ),
        },
        {
            title: t("studentExit.form.exitType"),
            dataIndex: "exitType",
            key: "exitType",
            render: (exitType: string) => (
                <ExitTypeBadge exitType={exitType as any} />
            ),
        },
        {
            title: t("studentExit.form.exitTime"),
            dataIndex: "exitTime",
            key: "exitTime",
            render: (v: string) => formatToLocaleDate(v, 'DD/MM/YYYY HH:mm') ?? "—",
        },
        {
            title: t("studentExit.form.status"),
            dataIndex: "status",
            key: "status",
            align: "center" as const,
            render: (status: string) => (
                <StudentExitStatusBadge status={status as any} />
            ),
        },
        {
            title: t("studentExit.form.recordedBy"),
            key: "recordedBy",
            render: (_: any, record: any) => (
                <span>
                    {record.recordedBy?.firstName} {record.recordedBy?.lastName}
                </span>
            ),
        },
        {
            title: t("created_at"),
            dataIndex: "createdAt",
            key: "createdAt",
            render: (v: string) => formatToLocaleDate(v) ?? "—",
        },
        {
            title: t("actions"),
            key: "actions",
            hidden: !ability.can("update", "StudentExit") && !ability.can("delete", "StudentExit"),
            render: (_: string, record: any) => {
                const actions: Action[] = [];

                if (record.status === 'pending' && ability.can("update", "StudentExit")) {
                    actions.push({
                        label: t("studentExit.status.completed"),
                        icon: "ti ti-checkbox",
                        actionType: "link",
                        onClick: () => handleComplete(record),
                    });
                    actions.push({
                        label: t("studentExit.status.rejected"),
                        icon: "ti ti-x",
                        actionType: "link",
                        onClick: () => handleReject(record),
                    });
                    // actions.push({
                    //     label: t("edit"),
                    //     icon: "ti ti-edit",
                    //     actionType: "link",
                    //     onClick: () => onEdit?.(record),
                    // });
                }

                // if (record.status === 'pending' || record.status === 'rejected' && ability.can("delete", "StudentExit")) {
                //     actions.push({
                //         label: t("delete"),
                //         icon: "ti ti-trash",
                //         actionType: "link",
                //         onClick: () => handleDelete(record),
                //     });
                // }

                return actions.length > 0 ? <ActionsDropdown actions={actions} /> : <i className="ti ti-lock"></i>;
            },
        },
    ];

    return (
        <div className="table-responsive">
            <Datatable
                rowKey="id"
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={true}
                dataPaginate={dataPaginate}
                onChange={onChange}
                expandable={{
                    expandedRowRender: (record: any) => record.notes ? (
                        <CustomAlert
                            type="info"
                            showIcon
                            message={t("studentExit.form.notes")}
                            description={record.notes}
                        />
                    ) : null,
                    rowExpandable: (record: any) => !!record.notes,
                    expandedRowClassName: () => "bg-transparent",
                }}
            />
        </div>
    );
};

export default StudentExitTable;